import React, { useRef, useEffect } from "react";

const SheetSizingGrid = ({
  sheetRows,
  selectedRowIndex,
  handleInputChange,
  handleAddRow,
  handleDeleteRow,
  handleRowClick,
  UnitSize,
  ItemTypeMaster,
  Ply,
  TypeMaster,
  CalculationTypeMaster,
  PrintDescription,
  ConverttoSoot,
  IsAgainstOrder = false,
  spdpGridRef
}) => {
  const inputRefs = useRef({});

  useEffect(() => {
    // Initialize refs for all rows
    sheetRows.forEach((_, rowIndex) => {
      if (!inputRefs.current[rowIndex]) {
        inputRefs.current[rowIndex] = {};
      }
    });
  }, [sheetRows]);

  const convertToFraction = (value, fieldName = '') => {
    // If value is empty or already contains "/", don't convert
    if (!value || value.includes('/')) {
      return value;
    }
    
    // Remove any existing fraction format and get the numeric part
    const numericValue = parseFloat(value);
    
    // If not a valid number, return original value
    if (isNaN(numericValue) || numericValue === 0) {
      return value;
    }
    
    // Handle negative and positive values specially for PlyMargin field
    if (fieldName === 'PlyMargin') {
      const isNegative = numericValue < 0;
      const absValue = Math.abs(numericValue);
      
      // Common fractions mapping (all in terms of eighths for consistency)
      const fractionMap = {
        0.125: "1/8",
        0.25: "1/4", // 2/8
        0.375: "3/8",
        0.5: "1/2", // 4/8
        0.625: "5/8",
        0.75: "3/4", // 6/8
        0.875: "7/8"
      };
      
      // Find the closest fraction match
      let closestFraction = null;
      let closestDiff = Infinity;
      
      for (const [decimal, fraction] of Object.entries(fractionMap)) {
        const diff = Math.abs(absValue - parseFloat(decimal));
        if (diff < closestDiff) {
          closestDiff = diff;
          closestFraction = fraction;
        }
      }
      
      // If we found a close match (within 0.01), use it
      if (closestFraction && closestDiff < 0.01) {
        return isNegative ? `-${closestFraction}` : closestFraction;
      }
    }
    
    // Default conversion logic for other fields
    // Convert to x/8 format first
    let numerator = Math.abs(numericValue);
    let denominator = 8;
    
    // Simplify the fraction by finding GCD
    const gcd = (a, b) => {
      while (b !== 0) {
        let temp = b;
        b = a % b;
        a = temp;
      }
      return a;
    };
    
    const commonDivisor = gcd(numerator, denominator);
    numerator = numerator / commonDivisor;
    denominator = denominator / commonDivisor;
    
    // Return simplified fraction with sign
    const sign = numericValue < 0 ? '-' : '';
    return `${sign}${numerator}/${denominator}`;
  };

  const handleFocus = (e) => {
    // Auto-select all content when input receives focus
    if (e.target.select) {
      e.target.select();
    }
  };

  const handleKeyDown = (e, rowIndex, fieldName) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      // Check if this is a margin field that needs conversion
      const marginFields = [
        'PlyMargin', 'LengthMar', 'LengthManualMargin', 'WidthMar', 
        'WidthManualMargin', 'HeightMar', 'HeightManualMargin', 'PinningMargin'
      ];
      
      if (marginFields.includes(fieldName)) {
        const currentValue = e.target.value;
        const convertedValue = convertToFraction(currentValue, fieldName);
        
        // Update the value if it changed
        if (convertedValue !== currentValue) {
          handleInputChange(rowIndex, fieldName, convertedValue);
        }
      }
      
      const nextField = getNextField(rowIndex, fieldName);
      
      if (nextField) {
        const { nextRowIndex, nextFieldName } = nextField;
        
        // Focus the next input
        setTimeout(() => {
          const nextInput = inputRefs.current[nextRowIndex]?.[nextFieldName];
          if (nextInput) {
            nextInput.focus();
          }
        }, 50);
      }
    }
  };

  const handleAddRowAndFocus = () => {
    // Only allow adding rows when it's not an order against
    if (IsAgainstOrder) {
      return;
    }
    
    const currentRowCount = sheetRows.length;
    handleAddRow();
    
    // Focus the Order Date field of the new row
    setTimeout(() => {
      const newRowOrderDateInput = inputRefs.current[currentRowCount]?.['OrderDate'];
      if (newRowOrderDateInput) {
        newRowOrderDateInput.focus();
      }
    }, 100);
  };

  const getNextField = (rowIndex, currentField) => {
    // Define field order based on whether it's an order against or not
    let fieldOrder;
    
    if (IsAgainstOrder) {
      // For order against, exclude action buttons
      fieldOrder = [
        'OrderDate', 'DOD2', 'CodeNo', 'F_UnitSize', 'F_ItemTypeMaster', 
        'F_PlyMaster', 'PlyMargin', 'Length', 'LengthMar', 'LengthManual', 
        'LengthManualMargin', 'Width', 'WidthMar', 'WidthManual', 'WidthManualMargin',
        'Height', 'HeightMar', 'HeightManual', 'HeightManualMargin', 'Pinning', 
        'PinningMargin', 'F_TypeMaster', 'InOut', 'F_CalculationTypeMaster', 
        'NoOfBox', 'SPDP', 'F_PrintDescriptionMaster', 'IsCompleted', 
        'IsPaste', 'IsPrint'
      ];
    } else {
      // For normal orders, include action buttons
      fieldOrder = [
        'OrderDate', 'DOD2', 'CodeNo', 'F_UnitSize', 'F_ItemTypeMaster', 
        'F_PlyMaster', 'PlyMargin', 'Length', 'LengthMar', 'LengthManual', 
        'LengthManualMargin', 'Width', 'WidthMar', 'WidthManual', 'WidthManualMargin',
        'Height', 'HeightMar', 'HeightManual', 'HeightManualMargin', 'Pinning', 
        'PinningMargin', 'F_TypeMaster', 'InOut', 'F_CalculationTypeMaster', 
        'NoOfBox', 'SPDP', 'F_PrintDescriptionMaster', 'IsCompleted', 
        'IsPaste', 'IsPrint', 'addButton'
      ];
    }

    // Fields to skip - after SPDP, jump directly to Print Description
    const fieldsToSkip = [
      'TLength', 'TLengthMargin', 'LengthIncre', 'LengthIncrement', 
      'THeight', 'THeightMargin', 'HeightIncre', 'HeightX', 'X', 
      'LengthX', 'SheetQty'
    ];

    // If current field is SPDP or any of the middle fields, jump to Print Description
    if (currentField === 'SPDP' || fieldsToSkip.includes(currentField)) {
      return {
        nextRowIndex: rowIndex,
        nextFieldName: 'F_PrintDescriptionMaster'
      };
    }

    const currentIndex = fieldOrder.indexOf(currentField);
    const currentRow = sheetRows[rowIndex];
    
    // Helper function to find next non-disabled field
    const findNextAvailableField = (startIndex, targetRowIndex) => {
      const targetRow = sheetRows[targetRowIndex];
      
      for (let i = startIndex; i < fieldOrder.length; i++) {
        const fieldName = fieldOrder[i];
        
        // Check if this field should be disabled
        if (!isFieldDisabled(fieldName, targetRow)) {
          return {
            nextRowIndex: targetRowIndex,
            nextFieldName: fieldName
          };
        }
      }
      
      // If no available field found in current row, try next row
      if (targetRowIndex < sheetRows.length - 1) {
        return findNextAvailableField(0, targetRowIndex + 1);
      }
      
      // If no next row, focus add button (only for normal orders)
      if (!IsAgainstOrder) {
        return {
          nextRowIndex: rowIndex,
          nextFieldName: 'addButton'
        };
      } else {
        // For order against, just return null since there's no add button
        return null;
      }
    };
    
    if (currentIndex < fieldOrder.length - 1) {
      // Find next available field starting from current index + 1
      return findNextAvailableField(currentIndex + 1, rowIndex);
    } else if (currentField === 'addButton' && !IsAgainstOrder) {
      // At add button, create new row and focus Order Date (only for normal orders)
      handleAddRowAndFocus();
      return null;
    } else if (rowIndex < sheetRows.length - 1) {
      // Move to first available field of next row
      return findNextAvailableField(0, rowIndex + 1);
    } else {
      // At last field of last row, focus add button (only for normal orders)
      if (!IsAgainstOrder) {
        return {
          nextRowIndex: rowIndex,
          nextFieldName: 'addButton'
        };
      } else {
        // For order against, just return null since there's no add button
        return null;
      }
    }
  };

  const setInputRef = (rowIndex, fieldName, element) => {
    if (!inputRefs.current[rowIndex]) {
      inputRefs.current[rowIndex] = {};
    }
    inputRefs.current[rowIndex][fieldName] = element;
    
    // Set spdpGridRef for the first row's Pinning field (only for normal orders)
    if (rowIndex === 0 && fieldName === 'Pinning' && spdpGridRef && !IsAgainstOrder) {
      spdpGridRef.current = element;
    }
  };

  const inputStyles = {
    width: "100%",
    padding: "6px 8px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontSize: "14px",
    backgroundColor: "#fff",
    transition: "all 0.3s ease",
    outline: "none"
  };

  const inputFocusStyles = `
    input:focus, select:focus {
      border: 2px solid #007bff !important;
      background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 50%, #90caf9 100%) !important;
      box-shadow: 0 0 15px rgba(0, 123, 255, 0.4), inset 0 1px 3px rgba(0, 123, 255, 0.2) !important;
      transform: scale(1.02) !important;
      z-index: 10 !important;
      position: relative !important;
    }
    
    input:focus::placeholder {
      color: #1976d2 !important;
      opacity: 0.8 !important;
    }
  `;

  const selectStyles = {
    ...inputStyles,
    appearance: "none",
    backgroundImage: "url('data:image/svg+xml;charset=US-ASCII,<svg width=\"12\" height=\"12\" xmlns=\"http://www.w3.org/2000/svg\"><path d=\"M3 5h6L6 9z\" fill=\"%23333\"/></svg>')",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 8px center",
    paddingRight: "24px"
  };

  const buttonFocusStyles = `
    button:focus {
      outline: none !important;
      border: 2px solid #007bff !important;
      background: linear-gradient(135deg, #28a745 0%, #20c997 50%, #17a2b8 100%) !important;
      box-shadow: 0 0 15px rgba(0, 123, 255, 0.6), 0 0 25px rgba(40, 167, 69, 0.3) !important;
      transform: scale(1.05) !important;
      z-index: 10 !important;
      position: relative !important;
    }
  `;

  // Add styles to the document head
  React.useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = inputFocusStyles + buttonFocusStyles;
    document.head.appendChild(styleSheet);
    
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  const tableStyles = {
    minWidth: "100%",
    borderCollapse: "collapse",
    border: "1px solid #e0e0e0",
    fontSize: "14px",
    backgroundColor: "#ffffff",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
  };

  const thStyles = {
    backgroundColor: "#f8f9fa",
    color: "#333",
    padding: "12px 8px",
    border: "1px solid #e0e0e0",
    fontWeight: "600",
    textAlign: "center",
    whiteSpace: "nowrap"
  };

  const tdStyles = {
    padding: "8px",
    border: "1px solid #e0e0e0",
    verticalAlign: "middle"
  };

  const buttonStyles = {
    padding: "6px 12px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    transition: "all 0.2s ease"
  };

  const addButtonStyles = {
    ...buttonStyles,
    backgroundColor: "#28a745",
    color: "white",
    "&:hover": {
      backgroundColor: "#218838"
    }
  };

  const deleteButtonStyles = {
    ...buttonStyles,
    backgroundColor: "#dc3545",
    color: "white",
    "&:hover": {
      backgroundColor: "#c82333"
    }
  };

  // Fields that should be disabled when IsAgainstOrder is true
  const fieldsToDisableAgainstOrder = [
    'OrderDate', 'DOD2', 'CodeNo', 'F_UnitSize', 'F_ItemTypeMaster',
    'F_PlyMaster', 'PlyMargin', 'Length', 'LengthMar', 'Width', 'WidthMar',
    'Height', 'HeightMar', 'LengthManual', 'LengthManualMargin', 
    'WidthManual', 'WidthManualMargin', 'HeightManual', 'HeightManualMargin',
    'F_ItemType'
  ];

  // 'Pinning', 'PinningMargin', 'F_TypeMaster', 'InOut', 'F_CalculationTypeMaster', 
  // 'NoOfBox',

  // Helper function to check if field should be disabled
  const isFieldDisabled = (fieldName, row) => {
    if (IsAgainstOrder && fieldsToDisableAgainstOrder.includes(fieldName)) {
      return true;
    }
    // Existing logic for unit size specific fields
    if (['LengthMar', 'LengthManualMargin', 'WidthMar', 'WidthManualMargin', 'HeightMar', 'HeightManualMargin'].includes(fieldName)) {
      return row.F_UnitSize === "1" || row.F_UnitSize === "2";
    }
    return false;
  };

  // Create disabled styles
  const getInputStyles = (isDisabled) => ({
    ...inputStyles,
    backgroundColor: isDisabled ? "#f5f5f5" : "#fff",
    cursor: isDisabled ? "not-allowed" : "text",
    opacity: isDisabled ? 0.7 : 1
  });

  const getSelectStyles = (isDisabled) => ({
    ...selectStyles,
    backgroundColor: isDisabled ? "#f5f5f5" : "#fff",
    cursor: isDisabled ? "not-allowed" : "pointer",
    opacity: isDisabled ? 0.7 : 1
  });

  return (
    <div className="table-wrapper" style={{ overflowX: "auto", padding: "16px" }}>
      <table style={tableStyles}>
        <thead>
          <tr>
            <th style={{ ...thStyles, minWidth: "100px", width: "80px" }}>S No</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "120px" }}>Order Date</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "120px" }}>DOD Date</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "180px" }}>Code No.</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "120px" }}>Unit</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "180px" }}>Item Type</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "400px" }}>Ply</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "80px" }}>Ply Mar</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "80px" }}>Length</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "70px" }}>L(in)</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "70px" }}>L(M)</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "70px" }}>L(in)</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "80px" }}>Width</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "70px" }}>W(in)</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "70px" }}>W(M)</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "70px" }}>W(in)</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "80px" }}>Height</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "70px" }}>H(in)</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "70px" }}>H(M)</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "70px" }}>H(in)</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "70px" }}>PF</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "70px" }}>PF(in)</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "380px" }}>Type</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "120px" }}>In/Out</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "400px" }}>Calculation</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "80px" }}>Box Qty</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "80px" }}>DP/SP</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "80px" }}>T Len</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "80px" }}>T(L) Mar</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "70px" }}>L(*)</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "70px" }}>L(+)</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "80px" }}>T Hei</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "80px" }}>T(H) Mar</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "70px" }}>H(*)</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "70px" }}>H</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "70px" }}>X</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "70px" }}>L</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "80px" }}>Sheet Qty</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "400px" }}>Print Description</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "100px" }}>Comp</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "100px" }}>Paste</th>
            <th style={{ ...thStyles, minWidth: "100px", width: "100px" }}>Print</th>
            {!IsAgainstOrder && (
              <th style={{ ...thStyles, minWidth: "100px", width: "120px" }}>Action</th>
            )}
          </tr>
        </thead>

        <tbody>
          {sheetRows.map((row, i) => (
            <tr
              key={i}
              onClick={() => handleRowClick(i)}
              style={{
                backgroundColor: selectedRowIndex === i ? "#f5f5f5" : "inherit",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "#f8f9fa"
                }
              }}
            >
              <td style={{ ...tdStyles, minWidth: "100px", width: "80px", textAlign: "center" }}>{i + 1}</td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "120px" }}>
                <input
                  ref={el => setInputRef(i, 'OrderDate', el)}
                  type="date"
                  value={row.OrderDate}
                  style={getInputStyles(isFieldDisabled('OrderDate', row))}
                  disabled={isFieldDisabled('OrderDate', row)}
                  onChange={e => !isFieldDisabled('OrderDate', row) && handleInputChange(i, "OrderDate", e.target.value)}
                  onKeyDown={e => handleKeyDown(e, i, 'OrderDate')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "120px" }}>
                <input
                  ref={el => setInputRef(i, 'DOD2', el)}
                  type="date"
                  value={row.DOD2}
                  style={getInputStyles(isFieldDisabled('DOD2', row))}
                  disabled={isFieldDisabled('DOD2', row)}
                  onChange={e => !isFieldDisabled('DOD2', row) && handleInputChange(i, "DOD2", e.target.value)}
                  onKeyDown={e => handleKeyDown(e, i, 'DOD2')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "150px", width: "180px" }}>
                <input
                  ref={el => setInputRef(i, 'CodeNo', el)}
                  type="text"
                  value={row.CodeNo}
                  style={getInputStyles(isFieldDisabled('CodeNo', row))}
                  disabled={isFieldDisabled('CodeNo', row)}
                  onChange={e => !isFieldDisabled('CodeNo', row) && handleInputChange(i, "CodeNo", e.target.value)}
                  onKeyDown={e => handleKeyDown(e, i, 'CodeNo')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "120px" }}>
                <select
                  ref={el => setInputRef(i, 'F_UnitSize', el)}
                  name="F_UnitSize"
                  value={row.F_UnitSize}
                  disabled={isFieldDisabled('F_UnitSize', row)}
                  onChange={e => !isFieldDisabled('F_UnitSize', row) && handleInputChange(i, "F_UnitSize", e.target.value)}
                  style={getSelectStyles(isFieldDisabled('F_UnitSize', row))}
                  onKeyDown={e => handleKeyDown(e, i, 'F_UnitSize')}
                  onFocus={handleFocus}
                >
                  <option value="">Select Unit</option>
                  {UnitSize &&
                    UnitSize.map(option => (
                      <option key={option.Id} value={option.Id}>
                        {option.Name}
                      </option>
                    ))}
                </select>
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "180px" }}>
                <select
                  ref={el => setInputRef(i, 'F_ItemTypeMaster', el)}
                  name="F_ItemTypeMaster"
                  value={row.F_ItemTypeMaster}
                  disabled={isFieldDisabled('F_ItemTypeMaster', row)}
                  onChange={e => !isFieldDisabled('F_ItemTypeMaster', row) && handleInputChange(i, "F_ItemTypeMaster", e.target.value)}
                  style={getSelectStyles(isFieldDisabled('F_ItemTypeMaster', row))}
                  onKeyDown={e => handleKeyDown(e, i, 'F_ItemTypeMaster')}
                  onFocus={handleFocus}
                >
                  {ItemTypeMaster &&
                    ItemTypeMaster.map(option => (
                      <option key={option.Id} value={option.Id}>
                        {option.Name}
                      </option>
                    ))}
                </select>
              </td>
              <td style={{ ...tdStyles, minWidth: "150px", width: "400px" }}>
                <select
                  ref={el => setInputRef(i, 'F_PlyMaster', el)}
                  name="F_PlyMaster"
                  value={row.F_PlyMaster}
                  disabled={isFieldDisabled('F_PlyMaster', row)}
                  onChange={e => !isFieldDisabled('F_PlyMaster', row) && handleInputChange(i, "F_PlyMaster", e.target.value)}
                  style={getSelectStyles(isFieldDisabled('F_PlyMaster', row))}
                  onKeyDown={e => handleKeyDown(e, i, 'F_PlyMaster')}
                  onFocus={handleFocus}
                >
                  <option value="">Select Ply</option>
                  {Ply &&
                    Ply.map(option => (
                      <option key={option.Id} value={option.Id}>
                        {option.Name}
                      </option>
                    ))}
                </select>
              </td>
              <td style={{ ...tdStyles, minWidth: "80px", width: "80px" }}>
                <input
                  ref={el => setInputRef(i, 'PlyMargin', el)}
                  type="text"
                  value={row.PlyMargin}
                  style={getInputStyles(isFieldDisabled('PlyMargin', row))}
                  disabled={isFieldDisabled('PlyMargin', row)}
                  onChange={e => !isFieldDisabled('PlyMargin', row) && handleInputChange(i, "PlyMargin", e.target.value)}
                  onKeyDown={e => handleKeyDown(e, i, 'PlyMargin')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "80px" }}>
                <input
                  ref={el => setInputRef(i, 'Length', el)}
                  type="text"
                  value={row.Length}
                  style={getInputStyles(isFieldDisabled('Length', row))}
                  disabled={isFieldDisabled('Length', row)}
                  onChange={e => !isFieldDisabled('Length', row) && handleInputChange(i, "Length", e.target.value)}
                  onKeyDown={e => handleKeyDown(e, i, 'Length')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "70px" }}>
                <input
                  ref={el => setInputRef(i, 'LengthMar', el)}
                  type="text"
                  value={row.LengthMar}
                  style={getInputStyles(isFieldDisabled('LengthMar', row))}
                  disabled={isFieldDisabled('LengthMar', row)}
                  onChange={e => !isFieldDisabled('LengthMar', row) && handleInputChange(i, "LengthMar", e.target.value)}
                  onKeyDown={e => handleKeyDown(e, i, 'LengthMar')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "70px" }}>
                <input
                  ref={el => setInputRef(i, 'LengthManual', el)}
                  type="text"
                  value={row.LengthManual}
                  style={getInputStyles(isFieldDisabled('LengthManual', row))}
                  disabled={isFieldDisabled('LengthManual', row)}
                  onChange={e => !isFieldDisabled('LengthManual', row) && handleInputChange(i, "LengthManual", e.target.value)}
                  onKeyDown={e => handleKeyDown(e, i, 'LengthManual')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "70px" }}>
                <input
                  ref={el => setInputRef(i, 'LengthManualMargin', el)}
                  type="text"
                  value={row.LengthManualMargin}
                  style={getInputStyles(isFieldDisabled('LengthManualMargin', row))}
                  disabled={isFieldDisabled('LengthManualMargin', row)}
                  onChange={e => !isFieldDisabled('LengthManualMargin', row) && handleInputChange(i, "LengthManualMargin", e.target.value)}
                  onKeyDown={e => handleKeyDown(e, i, 'LengthManualMargin')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "80px" }}>
                <input
                  ref={el => setInputRef(i, 'Width', el)}
                  type="text"
                  value={row.Width}
                  style={getInputStyles(isFieldDisabled('Width', row))}
                  disabled={isFieldDisabled('Width', row)}
                  onChange={e => !isFieldDisabled('Width', row) && handleInputChange(i, "Width", e.target.value)}
                  onKeyDown={e => handleKeyDown(e, i, 'Width')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "70px" }}>
                <input
                  ref={el => setInputRef(i, 'WidthMar', el)}
                  type="text"
                  value={row.WidthMar}
                  style={getInputStyles(isFieldDisabled('WidthMar', row))}
                  disabled={isFieldDisabled('WidthMar', row)}
                  onChange={e => !isFieldDisabled('WidthMar', row) && handleInputChange(i, "WidthMar", e.target.value)}
                  onKeyDown={e => handleKeyDown(e, i, 'WidthMar')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "70px" }}>
                <input
                  ref={el => setInputRef(i, 'WidthManual', el)}
                  type="text"
                  value={row.WidthManual}
                  style={getInputStyles(isFieldDisabled('WidthManual', row))}
                  disabled={isFieldDisabled('WidthManual', row)}
                  onChange={e => !isFieldDisabled('WidthManual', row) && handleInputChange(i, "WidthManual", e.target.value)}
                  onKeyDown={e => handleKeyDown(e, i, 'WidthManual')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "70px" }}>
                <input
                  ref={el => setInputRef(i, 'WidthManualMargin', el)}
                  type="text"
                  value={row.WidthManualMargin}
                  style={getInputStyles(isFieldDisabled('WidthManualMargin', row))}
                  disabled={isFieldDisabled('WidthManualMargin', row)}
                  onChange={e => !isFieldDisabled('WidthManualMargin', row) && handleInputChange(i, "WidthManualMargin", e.target.value)}
                  onKeyDown={e => handleKeyDown(e, i, 'WidthManualMargin')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "80px" }}>
                <input
                  ref={el => setInputRef(i, 'Height', el)}
                  type="text"
                  value={row.Height}
                  style={getInputStyles(isFieldDisabled('Height', row))}
                  disabled={isFieldDisabled('Height', row)}
                  onChange={e => !isFieldDisabled('Height', row) && handleInputChange(i, "Height", e.target.value)}
                  onKeyDown={e => handleKeyDown(e, i, 'Height')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "70px" }}>
                <input
                  ref={el => setInputRef(i, 'HeightMar', el)}
                  type="text"
                  value={row.HeightMar}
                  style={getInputStyles(isFieldDisabled('HeightMar', row))}
                  disabled={isFieldDisabled('HeightMar', row)}
                  onChange={e => !isFieldDisabled('HeightMar', row) && handleInputChange(i, "HeightMar", e.target.value)}
                  onKeyDown={e => handleKeyDown(e, i, 'HeightMar')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "70px" }}>
                <input
                  ref={el => setInputRef(i, 'HeightManual', el)}
                  type="text"
                  value={row.HeightManual}
                  style={getInputStyles(isFieldDisabled('HeightManual', row))}
                  disabled={isFieldDisabled('HeightManual', row)}
                  onChange={e => !isFieldDisabled('HeightManual', row) && handleInputChange(i, "HeightManual", e.target.value)}
                  onKeyDown={e => handleKeyDown(e, i, 'HeightManual')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "90px", width: "70px" }}>
                <input
                  ref={el => setInputRef(i, 'HeightManualMargin', el)}
                  type="text"
                  value={row.HeightManualMargin}
                  style={getInputStyles(isFieldDisabled('HeightManualMargin', row))}
                  disabled={isFieldDisabled('HeightManualMargin', row)}
                  onChange={e => !isFieldDisabled('HeightManualMargin', row) && handleInputChange(i, "HeightManualMargin", e.target.value)}
                  onKeyDown={e => handleKeyDown(e, i, 'HeightManualMargin')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "90px", width: "70px" }}>
                <input
                  ref={el => setInputRef(i, 'Pinning', el)}
                  type="text"
                  value={row.Pinning}
                  style={getInputStyles(isFieldDisabled('Pinning', row))}
                  onChange={e => handleInputChange(i, "Pinning", e.target.value)}
                  onKeyDown={e => handleKeyDown(e, i, 'Pinning')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "90px", width: "70px" }}>
                <input
                  ref={el => setInputRef(i, 'PinningMargin', el)}
                  type="text"
                  value={row.PinningMargin}
                  style={getInputStyles(isFieldDisabled('PinningMargin', row))}
                  onChange={e => handleInputChange(i, "PinningMargin", e.target.value)}
                  onKeyDown={e => handleKeyDown(e, i, 'PinningMargin')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "150px", width: "380px" }}>
                <select
                  ref={el => setInputRef(i, 'F_TypeMaster', el)}
                  name="F_TypeMaster"
                  value={row.F_TypeMaster}
                  disabled={isFieldDisabled('F_TypeMaster', row)}
                  onChange={e => !isFieldDisabled('F_TypeMaster', row) && handleInputChange(i, "F_TypeMaster", e.target.value)}
                  style={getSelectStyles(isFieldDisabled('F_TypeMaster', row))}
                  onKeyDown={e => handleKeyDown(e, i, 'F_TypeMaster')}
                  onFocus={handleFocus}
                >
                  <option value="">Select</option>
                  {TypeMaster &&
                    TypeMaster.map(option => (
                      <option key={option.Id} value={option.Id}>
                        {option.Name}
                      </option>
                    ))}
                </select>
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "120px" }}>
                <select
                  ref={el => setInputRef(i, 'InOut', el)}
                  value={row.InOut}
                  disabled={isFieldDisabled('InOut', row)}
                  onChange={e => !isFieldDisabled('InOut', row) && handleInputChange(i, "InOut", e.target.value)}
                  style={getSelectStyles(isFieldDisabled('InOut', row))}
                  onKeyDown={e => handleKeyDown(e, i, 'InOut')}
                  onFocus={handleFocus}
                >
                  <option value="">Select</option>
                  <option value="1">Inside</option>
                  <option value="2">Outside</option>
                </select>
              </td>
              <td style={{ ...tdStyles, minWidth: "150px", width: "400px" }}>
                <select
                  ref={el => setInputRef(i, 'F_CalculationTypeMaster', el)}
                  name="F_CalculationTypeMaster"
                  value={row.F_CalculationTypeMaster}
                  disabled={isFieldDisabled('F_CalculationTypeMaster', row)}
                  onChange={e => !isFieldDisabled('F_CalculationTypeMaster', row) && handleInputChange(i, "F_CalculationTypeMaster", e.target.value)}
                  style={getSelectStyles(isFieldDisabled('F_CalculationTypeMaster', row))}
                  onKeyDown={e => handleKeyDown(e, i, 'F_CalculationTypeMaster')}
                  onFocus={handleFocus}
                >
                  <option value="">Select Calculation</option>
                  {CalculationTypeMaster &&
                    CalculationTypeMaster.map(option => (
                      <option key={option.Id} value={option.Id}>
                        {option.Name}
                      </option>
                    ))}
                </select>
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "80px" }}>
                <input
                  ref={el => setInputRef(i, 'NoOfBox', el)}
                  type="number"
                  value={row.NoOfBox}
                  style={getInputStyles(isFieldDisabled('NoOfBox', row))}
                  disabled={isFieldDisabled('NoOfBox', row)}
                  onChange={e => !isFieldDisabled('NoOfBox', row) && handleInputChange(i, "NoOfBox", e.target.value)}
                  onKeyDown={e => handleKeyDown(e, i, 'NoOfBox')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "80px" }}>
                <select
                  ref={el => setInputRef(i, 'SPDP', el)}
                  value={row.SPDP}
                  onChange={e => handleInputChange(i, "SPDP", e.target.value)}
                  style={getSelectStyles(isFieldDisabled('SPDP', row))}
                  onKeyDown={e => handleKeyDown(e, i, 'SPDP')}
                  onFocus={handleFocus}
                >
                  <option value="">Select</option>
                  <option value="1">DP</option>
                  <option value="2">SP</option>
                  <option value="3">FP</option>
                </select>
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "80px" }}>
                <input
                  ref={el => setInputRef(i, 'TLength', el)}
                  type="number"
                  value={row.TLength}
                  style={getInputStyles(isFieldDisabled('TLength', row))}
                  onChange={e => handleInputChange(i, "TLength", e.target.value)}
                  onKeyDown={e => handleKeyDown(e, i, 'TLength')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "80px" }}>
                <input
                  ref={el => setInputRef(i, 'TLengthMargin', el)}
                  type="text"
                  value={row.TLengthMargin}
                  style={getInputStyles(isFieldDisabled('TLengthMargin', row))}
                  onChange={e => handleInputChange(i, "TLengthMargin", e.target.value)}
                  onKeyDown={e => handleKeyDown(e, i, 'TLengthMargin')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "70px" }}>
                <input
                  ref={el => setInputRef(i, 'LengthIncre', el)}
                  type="number"
                  value={row.LengthIncre}
                  style={getInputStyles(isFieldDisabled('LengthIncre', row))}
                  onChange={e => handleInputChange(i, "LengthIncre", e.target.value)}
                  onKeyDown={e => handleKeyDown(e, i, 'LengthIncre')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "70px" }}>
                <input
                  ref={el => setInputRef(i, 'LengthIncrement', el)}
                  type="number"
                  value={row.LengthIncrement}
                  style={getInputStyles(isFieldDisabled('LengthIncrement', row))}
                  onChange={e => handleInputChange(i, "LengthIncrement", e.target.value)}
                  onKeyDown={e => handleKeyDown(e, i, 'LengthIncrement')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "80px" }}>
                <input
                  ref={el => setInputRef(i, 'THeight', el)}
                  type="number"
                  value={row.THeight}
                  style={getInputStyles(isFieldDisabled('THeight', row))}
                  onChange={e => handleInputChange(i, "THeight", e.target.value)}
                  onKeyDown={e => handleKeyDown(e, i, 'THeight')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "80px" }}>
                <input
                  ref={el => setInputRef(i, 'THeightMargin', el)}
                  type="text"
                  value={row.THeightMargin}
                  style={getInputStyles(isFieldDisabled('THeightMargin', row))}
                  onChange={e => handleInputChange(i, "THeightMargin", e.target.value)}
                  onKeyDown={e => handleKeyDown(e, i, 'THeightMargin')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "70px" }}>
                <input
                  ref={el => setInputRef(i, 'HeightIncre', el)}
                  type="number"
                  value={row.HeightIncre}
                  style={getInputStyles(isFieldDisabled('HeightIncre', row))}
                  onChange={e => handleInputChange(i, "HeightIncre", e.target.value)}
                  onKeyDown={e => handleKeyDown(e, i, 'HeightIncre')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "70px" }}>
                <input
                  ref={el => setInputRef(i, 'HeightX', el)}
                  type="text"
                  value={row.HeightX}
                  style={getInputStyles(isFieldDisabled('HeightX', row))}
                  onChange={e => handleInputChange(i, "HeightX", e.target.value)}
                  onKeyDown={e => handleKeyDown(e, i, 'HeightX')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "70px" }}>
                <input
                  ref={el => setInputRef(i, 'X', el)}
                  type="text"
                  value={row.X}
                  style={getInputStyles(isFieldDisabled('X', row))}
                  readOnly
                  onKeyDown={e => handleKeyDown(e, i, 'X')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "70px" }}>
                <input
                  ref={el => setInputRef(i, 'LengthX', el)}
                  type="text"
                  value={row.LengthX}
                  style={getInputStyles(isFieldDisabled('LengthX', row))}
                  onChange={e => handleInputChange(i, "LengthX", e.target.value)}
                  onKeyDown={e => handleKeyDown(e, i, 'LengthX')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "80px" }}>
                <input
                  ref={el => setInputRef(i, 'SheetQty', el)}
                  type="number"
                  value={row.SheetQty}
                  style={getInputStyles(isFieldDisabled('SheetQty', row))}
                  onChange={e => handleInputChange(i, "SheetQty", e.target.value)}
                  onKeyDown={e => handleKeyDown(e, i, 'SheetQty')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "400px" }}>
                <select
                  ref={el => setInputRef(i, 'F_PrintDescriptionMaster', el)}
                  name="F_PrintDescriptionMaster"
                  value={row.F_PrintDescriptionMaster}
                  onChange={e => handleInputChange(i, "F_PrintDescriptionMaster", e.target.value)}
                  style={getSelectStyles(isFieldDisabled('F_PrintDescriptionMaster', row))}
                  onKeyDown={e => handleKeyDown(e, i, 'F_PrintDescriptionMaster')}
                  onFocus={handleFocus}
                >
                  <option value="">Select Print Description</option>
                  {PrintDescription &&
                    PrintDescription.map(option => (
                      <option key={option.Id} value={option.Id}>
                        {option.Name}
                      </option>
                    ))}
                </select>
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "100px" }}>
                <input
                  ref={el => setInputRef(i, 'IsCompleted', el)}
                  type="checkbox"
                  checked={row.IsCompleted}
                  style={getInputStyles(isFieldDisabled('IsCompleted', row))}
                  onChange={e => handleInputChange(i, "IsCompleted", e.target.checked)}
                  onKeyDown={e => handleKeyDown(e, i, 'IsCompleted')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "100px" }}>
                <input
                  ref={el => setInputRef(i, 'IsPaste', el)}
                  type="checkbox"
                  checked={row.IsPaste}
                  style={getInputStyles(isFieldDisabled('IsPaste', row))}
                  onChange={e => handleInputChange(i, "IsPaste", e.target.checked)}
                  onKeyDown={e => handleKeyDown(e, i, 'IsPaste')}
                  onFocus={handleFocus}
                />
              </td>
              <td style={{ ...tdStyles, minWidth: "100px", width: "100px" }}>
                <input
                  ref={el => setInputRef(i, 'IsPrint', el)}
                  type="checkbox"
                  checked={row.IsPrint}
                  style={getInputStyles(isFieldDisabled('IsPrint', row))}
                  onChange={e => handleInputChange(i, "IsPrint", e.target.checked)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      // Check if there's a next row
                      if (i + 1 < sheetRows.length) {
                        // Focus the SPDP field of the next row
                        setTimeout(() => {
                          const nextRowSPDPInput = inputRefs.current[i + 1]?.['Pinning'];
                          if (nextRowSPDPInput) {
                            nextRowSPDPInput.focus();
                          }
                        }, 50);
                      } else {
                        // If it's the last row, focus the Save button
                        const saveButton = document.getElementById('saveButton');
                        if (saveButton) {
                          saveButton.focus();
                        }
                      }
                    } else {
                      handleKeyDown(e, i, 'IsPrint');
                    }
                  }}
                  // onKeyDown={e => handleKeyDown(e, i, 'Pinning')}
                  onFocus={handleFocus}
                />
              </td>
              {!IsAgainstOrder && (
                <td style={{ ...tdStyles, minWidth: "100px", width: "120px" }}>
                  <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                    <button
                      ref={el => setInputRef(i, 'addButton', el)}
                      style={addButtonStyles}
                      onClick={e => {
                        e.stopPropagation();
                        if (!IsAgainstOrder) {
                          handleAddRowAndFocus();
                        }
                      }}
                      onKeyDown={e => handleKeyDown(e, i, 'addButton')}
                      onFocus={handleFocus}
                    >
                      +
                    </button>
                    <button
                      style={deleteButtonStyles}
                      onClick={e => {
                        e.stopPropagation();
                        if (!IsAgainstOrder) {
                          handleDeleteRow(i);
                        }
                      }}
                      onFocus={handleFocus}
                    >
                      -
                    </button>
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default SheetSizingGrid; 